// Read-only presentation of stored payroll facts. Never approves a year-end review.
(function registerYearClosingPayrollOverview(global: any) {
  const list = (value: any): any[] => Array.isArray(value) ? value : [];
  const amount = (value: any) => Number.isFinite(Number(value)) ? Number(value) : 0;
  const money = (value: number) => Math.round(value * 100) / 100;
  const posted = (run: any) => ['approved', 'paid'].includes(run.status);
  const period = (run: any) => String(run.period || run.paymentDate || '').slice(0, 7);
  const sum = (rows: any[], key: string) => money(rows.reduce((total, row) => total + amount(row[key]), 0));
  const groups = [
    { key: 'complete', confirmations: ['payrollRunsReviewed', 'ameldingReviewed'] },
    { key: 'balances', confirmations: ['ledgerReconciled', 'holidayPayReviewed', 'employeeLiabilitiesReviewed'] },
    { key: 'payments', confirmations: ['paymentsReviewed', 'withholdingReviewed', 'agaReviewed'] },
  ];
  const build = ({ state, year, supported, trial }: any) => {
    if (!supported) return null;
    const all = list(state.payrollRuns).filter((run) => !['reversed', 'annulled'].includes(run.status));
    const runs = all.filter((run) => period(run).slice(0, 4) === String(year));
    const booked = runs.filter(posted);
    const lines = booked.flatMap((run) => list(run.lines));
    const obligations = global.PlummePayrollObligations;
    const months = Array.from({ length: 12 }, (_, index) => {
      const key = `${year}-${String(index + 1).padStart(2, '0')}`;
      const entries = runs.filter((run) => period(run) === key);
      const approved = entries.filter(posted);
      return { key, posted: approved.length, drafts: entries.filter((run) => !posted(run)).length,
        unreported: approved.filter((run) => !run.altinnSent).length,
        unpaid: approved.filter((run) => !run.paymentStatus?.paidAt && !run.paidAt).length };
    });
    // Public liabilities follow the payment/reporting year, not the payroll period.
    // Keep buckets separate so an overpayment cannot hide another unpaid obligation.
    const paymentRuns = all.filter((run) => posted(run) && obligations.reportingDate(run).slice(0, 4) === String(year));
    const payments = Object.entries(state.payrollTaxPaymentsByYear || {}).flatMap(([storedYear, entries]) =>
      list(entries).filter((entry) => entry.status !== 'reversed').map((entry) => ({ ...entry, storedYear: Number(storedYear) })));
    const paymentsForTerm = (term: number, kind: string) => payments.filter((payment) =>
      Number(payment.term) === term && Number(payment.reportingYear || payment.year || payment.storedYear) === year &&
      (year < 2026 ? !payment.kind || payment.kind === kind : payment.kind === kind));
    const balances = Array.from({ length: 6 }, (_, index) => {
      const term = index + 1;
      const termRuns = paymentRuns.filter((run) => Math.ceil(Number(obligations.reportingDate(run).slice(5, 7)) / 2) === term);
      const aga = obligations.agaForTerm(state, year, term);
      const agaPaid = sum(paymentsForTerm(term, 'aga'), 'agaAmount');
      const tax = money(termRuns.reduce((total, run) => total + obligations.runWithholding(run), 0));
      const openTax = year < 2026 ? Math.max(0, money(tax - sum(paymentsForTerm(term, 'withholding'), 'taxAmount')))
        : money(termRuns.reduce((total, run) => total + Math.max(0, money(obligations.runWithholding(run) - sum(obligations.withholdingAllocation(state, run).matched, 'taxAmount'))), 0));
      return { term, openTax, openAga: Math.max(0, money(aga - agaPaid)) };
    });
    const totals = { gross: sum(lines, 'grossSalary'), tax: sum(lines, 'taxDeduction'),
      holiday: sum(lines, 'holidayPayAccrual'), aga: money(booked.reduce((total, run) => total + obligations.runSalaryAga(run), 0)) };
    const accounts = list(trial).flatMap((group) => list(group.accounts));
    const movement = (codes: string[]) => money(accounts.filter((account) => codes.includes(String(account.nr)))
      .reduce((total, account) => total + amount(account.endring), 0));
    const closing = (prefixes: string[]) => money(accounts.filter((account) => prefixes.some((prefix) => String(account.nr).startsWith(prefix)))
      .reduce((total, account) => total + amount(account.ut), 0));
    const ledger = accounts.length ? {
      gross: movement(['5000', '5001', '5010', '5090']),
      aga: movement(['5400']),
      holiday: movement(['5092']),
      employeeLiability: closing(['2930']),
      holidayLiability: closing(['294']),
      holidayAgaLiability: closing(['2780']),
    } : null;
    const pensionAga = money(all.filter(posted).filter((run) => String(run.paymentStatus?.pensionPaidAt || '').slice(0, 4) === String(year)).reduce((total, run) => total + obligations.runPensionAga(run), 0)
      + list(state.pensionPremiumPayments).filter((payment) => payment.status !== 'reversed' && String(payment.date || '').slice(0, 4) === String(year)).reduce((total, payment) => total + amount(payment.employerContribution), 0));
    const comparisonTotals = { ...totals, aga: money(totals.aga + pensionAga) };
    const comparisons = ledger ? (['gross', 'aga', 'holiday'] as const).map((key) =>
      ({ key, payroll: comparisonTotals[key], ledger: ledger[key], difference: money(ledger[key] - comparisonTotals[key]) })) : [];
    return { year, months, totals, ledger, comparisons, pensionAga,
      ledgerBalanced: Boolean(ledger) && comparisons.length === 3 && comparisons.every((row) => Math.abs(row.difference) <= 0.005) && booked.length > 0 && booked.every((run) => list(run.lines).length && list(run.payrollPostingRows).length),
      booked: booked.length, drafts: runs.filter((run) => !posted(run)).length,
      unknownPeriod: all.filter((run) => !/^\d{4}-(0[1-9]|1[0-2])$/.test(period(run))).length,
      incomplete: booked.filter((run) => !list(run.lines).length || !list(run.payrollPostingRows).length).length,
      balances, openTax: sum(balances, 'openTax'), openAga: sum(balances, 'openAga') };
  };
  global.PlummeYearClosingPayrollOverview = { build, groups };
})(window);
